import React, { useEffect, useState } from "react"
import cogoToast from "cogo-toast";
import TextInput from "../../../../Common/components/text-input";
import TextAreaInput from "../../../../Common/components/text-area-input";
import ImageInput from "../../../../Common/components/image-input";
import SelectInput from "../../../../Common/components/select-input";
import { useGetUserGroupsQuery } from "../../../../../../redux/apiSlice";
import useColorThief from "use-color-thief";
import Compressor from 'compressorjs';
import { useFormikContext } from "formik";

const to0xColor = (value) => {
    if (!value) return "";
    const v = `${value}`.trim();
    if (/^0x[0-9a-fA-F]{6}$/.test(v)) return `0x${v.slice(2).toUpperCase()}`;
    if (/^#[0-9a-fA-F]{6}$/.test(v)) return `0x${v.slice(1).toUpperCase()}`;
    if (/^[0-9a-fA-F]{6}$/.test(v)) return `0x${v.toUpperCase()}`;
    return v;
}

const toHashColor = (value) => {
    if (!value) return "#000000";
    const v = `${value}`.trim();
    if (/^0x[0-9a-fA-F]{6}$/.test(v)) return `#${v.slice(2).toUpperCase()}`;
    if (/^#[0-9a-fA-F]{6}$/.test(v)) return `#${v.slice(1).toUpperCase()}`;
    if (/^[0-9a-fA-F]{6}$/.test(v)) return `#${v.toUpperCase()}`;
    return "#000000";
}

const Tab1 = ({ tab, onValidate, setFieldValue, includeImageFields = true }) => {
    const { values } = useFormikContext();

    const [submitAttempted, setSubmitAttempted] = useState(false)
    const { isFetching, data: userGroups, error } = useGetUserGroupsQuery()

    const [source, setSource] = useState({ banner: "", logo: "", webSpotlightImage: "", mobileSpotlightImage: "" })
    const { color: bannerBackgroundColor } = useColorThief(source.banner, {
        format: 'hex',
        colorCount: 10,
        quality: 10,
    });

    useEffect(() => {
        if (!includeImageFields) return;
        if (!bannerBackgroundColor) return;
        if (values?.bannerBackgroundColor) return;
        setFieldValue("bannerBackgroundColor", to0xColor(bannerBackgroundColor))
    }, [bannerBackgroundColor, includeImageFields, setFieldValue, values?.bannerBackgroundColor])

    const { color: logoBackgroundColor } = useColorThief(source.logo, {
        format: 'hex',
        colorCount: 10,
        quality: 10,
    });

    useEffect(() => {
        if (!includeImageFields) return;
        if (!logoBackgroundColor) return;
        if (values?.logoBackgroundColor) return;
        setFieldValue("logoBackgroundColor", to0xColor(logoBackgroundColor))
    }, [includeImageFields, logoBackgroundColor, setFieldValue, values?.logoBackgroundColor])

    const onFileSelected = (e, fieldName) => {
        const file = e.target.files[0];

        new Compressor(file, {
            maxHeight: 480,
            quality: 1,
            success: (compressedResult) => {
                const resizedFile = new File([compressedResult], file?.name)
                
                setSource({ ...source, [fieldName]: URL.createObjectURL(resizedFile) })
                setFieldValue(fieldName, resizedFile)
                validateFile(resizedFile);
            },
        });
    }

    const validateFile = (file) => {
        const fileName = file.name?.toLowerCase();
        if (!(fileName.endsWith(".jpg") || fileName.endsWith(".png") || fileName.endsWith(".jpeg"))) {
            cogoToast.error("Invalid File Selection");
            return
        }

        if (file.size > 10000000) {
            cogoToast.error("File too large (10mb Max)");
        }
    }

    const handleSubmit = _ => {
        onValidate();
        setSubmitAttempted(true)
    }

    return (
        <div className="form mt-0 mt-lg-10">
            <div
                className="pb-5"
                data-wizard-type="step-content"
                data-wizard-state={tab === 1 ? "current" : ""}
            >
                <div className="mb-10 h3 font-weight-bold text-dark">Theme Details</div>
                <div className={"row"}>
                    <TextInput
                        label="Theme Name*"
                        name="name"
                        type="text"
                        shouldShowError={submitAttempted}
                    />
                </div>
                <TextAreaInput
                    label="Description*"
                    name="description"
                    rows="3"
                    shouldShowError={submitAttempted}
                />
                <div className="row">
                    <SelectInput
                        label="Status"
                        name="status"
                        shouldShowError={submitAttempted}
                        options={["active", "pending"]}
                    />
                    <SelectInput
                        label="User Group"
                        name="userGroup"
                        shouldShowError={submitAttempted}
                        options={userGroups?.map((userGroup) => userGroup?.name?.toLowerCase())}
                    />
                </div>
                {
                    includeImageFields &&
                    <>
                        <div className="row">
                            <ImageInput
                                label="Banner*"
                                name="banner"
                                onChange={(e) => onFileSelected(e, "banner")}
                                shouldShowError={submitAttempted}
                            />
                            <ImageInput
                                label="Logo*"
                                name="logo"
                                onChange={(e) => onFileSelected(e, "logo")}
                                shouldShowError={submitAttempted}
                            />
                        </div>
                        <div className="row">
                            <TextInput
                                label="Banner Background Color*"
                                name="bannerBackgroundColor"
                                type="text"
                                placeholder="0xRRGGBB"
                                shouldShowError={submitAttempted}
                            />
                            <TextInput
                                label="Logo Background Color*"
                                name="logoBackgroundColor"
                                type="text"
                                placeholder="0xRRGGBB"
                                shouldShowError={submitAttempted}
                            />
                        </div>
                        <div className="row">
                            <div className="form-group col">
                                <label className={"capitalize text-sm"}>Banner Color Picker</label>
                                <input
                                    className={"form-control"}
                                    type="color"
                                    value={toHashColor(values?.bannerBackgroundColor)}
                                    onChange={(e) => setFieldValue("bannerBackgroundColor", to0xColor(e.target.value))}
                                />
                            </div>
                            <div className="form-group col">
                                <label className={"capitalize text-sm"}>Logo Color Picker</label>
                                <input
                                    className={"form-control"}
                                    type="color"
                                    value={toHashColor(values?.logoBackgroundColor)}
                                    onChange={(e) => setFieldValue("logoBackgroundColor", to0xColor(e.target.value))}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <ImageInput
                                label="Spotlight Image (Web)*"
                                name="webSpotlightImage"
                                onChange={(e) => onFileSelected(e, "webSpotlightImage")}
                                shouldShowError={submitAttempted}
                            />
                            <ImageInput
                                label="Spotlight Image (Mobile)*"
                                name="mobileSpotlightImage"
                                onChange={(e) => onFileSelected(e, "mobileSpotlightImage")}
                                shouldShowError={submitAttempted}
                            />
                        </div>
                    </>
                }

                {/* <!--begin: Wizard Actions--> */}
                <div className="text-right border-top mt-5 pt-10">
                    <button
                        onClick={handleSubmit}
                        type="button"
                        className="btn btn-primary font-weight-bolder text-uppercase px-9 py-4"
                    >
                        Next
                    </button>
                </div>
                {/* <!--end: Wizard Actions--> */}
            </div>
        </div>
    )
}

export default Tab1;
